import React from 'react';
import { MediaFile } from '../../types';
import { MediaService } from '../../services/mediaService';

interface MediaPreviewProps {
  mediaFiles: MediaFile[];
  onRemove: (index: number) => void;
  className?: string;
}

const MediaPreview: React.FC<MediaPreviewProps> = ({
  mediaFiles,
  onRemove,
  className = ''
}) => {
  if (mediaFiles.length === 0) return null;

  const renderMediaItem = (mediaFile: MediaFile, index: number) => {
    const { type, url, thumbnail, duration, file } = mediaFile;

    return (
      <div key={index} className="relative group rounded-xl overflow-hidden bg-gradient-to-br from-gray-900/60 to-emerald-900/40 shadow-cosmic-neon">
        {type === 'video' ? (
          <div className="relative aspect-video bg-black flex items-center justify-center">
            {/* Thumbnail de la vidéo */}
            {thumbnail ? (
              <img
                src={thumbnail}
                alt={`Aperçu vidéo ${index + 1}`}
                className="w-full h-full object-cover rounded-xl"
              />
            ) : (
              <div className="w-full h-full flex items-center justify-center bg-gray-800 rounded-xl">
                <svg className="w-12 h-12 text-emerald-300" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M8 5v14l11-7z"/>
                </svg>
              </div>
            )}
            
            {/* Overlay avec icône play */}
            <div className="absolute inset-0 bg-black bg-opacity-40 flex items-center justify-center rounded-xl">
              <div className="w-16 h-16 bg-emerald-600/80 rounded-full flex items-center justify-center backdrop-blur-sm">
                <svg className="w-8 h-8 text-white ml-1" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M8 5v14l11-7z"/>
                </svg>
              </div>
            </div>
            
            {/* Durée */}
            {duration && (
              <div className="absolute bottom-2 right-2 bg-black bg-opacity-70 text-white text-xs px-2 py-1 rounded-lg">
                {MediaService.formatDuration(duration)}
              </div>
            )}
            
            {/* Taille du fichier */}
            <div className="absolute top-2 left-2 bg-black bg-opacity-70 text-white text-xs px-2 py-1 rounded-lg">
              {MediaService.formatFileSize(file.size)}
            </div>
          </div>
        ) : (
          <div className="relative aspect-square">
            <img
              src={url}
              alt={`Aperçu ${index + 1}`}
              className="w-full h-full object-cover rounded-xl transition-transform duration-300 group-hover:scale-105"
            />
            
            {/* Indicateur GIF */}
            {type === 'gif' && (
              <div className="absolute top-2 left-2 bg-emerald-600 text-white text-xs font-bold px-2 py-1 rounded-lg">
                GIF
              </div>
            )}
          </div>
        )}
        
        {/* Bouton de suppression */}
        <button
          type="button"
          onClick={() => onRemove(index)}
          className="absolute top-2 right-2 bg-red-500/90 hover:bg-red-600 text-white rounded-full w-7 h-7 flex items-center justify-center text-base transition-all duration-200 backdrop-blur-sm shadow-lg opacity-0 group-hover:opacity-100"
          title="Supprimer ce média"
        >
          ×
        </button>
        
        {/* Overlay d'information au survol */}
        <div className="absolute inset-0 bg-black bg-opacity-0 hover:bg-opacity-30 transition-all duration-200 rounded-xl flex items-end opacity-0 group-hover:opacity-100">
          <div className="w-full p-3 bg-gradient-to-t from-black/60 to-transparent rounded-b-xl">
            <p className="text-white text-sm font-medium truncate">
              {file.name}
            </p>
            <p className="text-emerald-300 text-xs">
              {MediaService.formatFileSize(file.size)}
              {type === 'video' && duration && ` • ${MediaService.formatDuration(duration)}`}
            </p>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className={`mt-4 ${className}`}>
      {/* Grille responsive selon le nombre d'éléments */}
      <div className={`grid gap-3 ${
        mediaFiles.length === 1 ? 'grid-cols-1' : 
        mediaFiles.length === 2 ? 'grid-cols-2' :
        mediaFiles.length === 3 ? 'grid-cols-3' :
        'grid-cols-2'
      }`}>
        {mediaFiles.map((mediaFile, index) => renderMediaItem(mediaFile, index))}
      </div>
      
      {/* Informations supplémentaires */}
      {mediaFiles.length > 0 && (
        <div className="mt-2 flex justify-between items-center text-xs text-emerald-300/60">
          <span>
            {mediaFiles.length} {mediaFiles.length === 1 ? 'média' : 'médias'} sélectionné{mediaFiles.length > 1 ? 's' : ''}
          </span>
          <span>
            Total: {MediaService.formatFileSize(
              mediaFiles.reduce((total, mf) => total + mf.file.size, 0)
            )}
          </span>
        </div>
      )}
    </div>
  );
};

export default MediaPreview;
